import React, { useContext } from "react";
import { motion } from "framer-motion";
import { CursorContext } from "../context/CursorContext";
import { transition1 } from "../transitions";
import { ScrollLink } from "../components/ScrollLink";
import { BsArrowRight, BsChatQuote } from "react-icons/bs";
import { testimonials } from "../Data";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.65, ease: "easeOut" },
  }),
};

// 🧩 Testimonial data — replace avatar initials with real photos via `photo` field if available

const Testimonials = () => {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);

  return (
    <motion.section
      id="testimonials"
      className="section snap-start min-h-screen flex flex-col lg:pt-36 pt-24 pb-20 overflow-y-auto"
      initial={{ opacity: 0, y: "5%" }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.15 }}
      exit={{ opacity: 0, y: "5%" }}
      transition={transition1}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      <div className="container mx-auto px-6 flex flex-col h-full max-w-5xl">
        {/* ── Header ── */}
        <motion.div
          className="mb-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <motion.h1 className="h1 font-secondary" variants={fadeUp} custom={0}>
            Reflections
          </motion.h1>
          <motion.p
            className="mt-2 text-sm leading-relaxed max-w-3xl"
            variants={fadeUp}
            custom={1}
          >
            Small notes from the people who shared the journey, the challenges,
            and the moments behind the work.
          </motion.p>
        </motion.div>

        {/* ── Cards Grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 flex-1 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-400 scrollbar-track-transparent pb-2">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              className="relative rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/40 dark:bg-white/5 backdrop-blur-sm p-6 flex flex-col gap-4 group"
              variants={fadeUp}
              custom={i + 2}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.15 }}
              whileHover={{ y: -5, transition: { duration: 0.25 } }}
            >
              {/* quote icon */}
              <div
                className="text-3xl opacity-20 group-hover:opacity-40 transition-opacity"
                style={{ color: t.color }}
              >
                <BsChatQuote />
              </div>

              {/* message */}
              <p className="text-sm leading-relaxed text-justify flex-1">
                "{t.message}"
              </p>

              {/* divider */}
              <div className="h-px w-full bg-slate-200 dark:bg-slate-700" />

              {/* person */}
              <div className="flex items-center gap-3">
                {/* avatar circle */}
                <motion.div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white"
                  style={{ backgroundColor: t.color }}
                  whileHover={{ scale: 1.1 }}
                >
                  {t.initials}
                </motion.div>

                <div>
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-[11px] text-slate-400 tracking-wide">
                    {t.role}
                  </div>
                </div>
              </div>

              {/* subtle color accent on hover */}
              <div
                className="absolute inset-x-0 bottom-0 h-[3px] rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ backgroundColor: t.color }}
              />
            </motion.div>
          ))}
        </div>

        {/* ── CTA ── */}
        <motion.div
          className="shrink-0 mt-5 mb-1"
          variants={fadeUp}
          custom={7}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          <ScrollLink
            section="contact"
            className="btn max-w-fit hover:scale-105 transition-transform duration-300"
          >
            Get in Touch
            <span className="ml-1">
              <BsArrowRight />
            </span>
          </ScrollLink>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Testimonials;
